// src/features/streams/components/StreamEncoderSetupModal.tsx

import { Modal, Stack, TextInput, Text, Group, CopyButton, Button, Divider, Box } from '@mantine/core';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import type { StreamViewData, StreamOutputData } from '@/lib/types/Stream';
import { StreamUtils } from '../StreamUtils';
import { ResellerBrand } from '@/shared/utils/resellerBrand';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import type { IBackendLiveData } from '@/lib/types/Customer';

interface StreamEncoderSetupModalProps {
  opened: boolean;
  onClose: () => void;
  stream: StreamViewData;
  backendMap: Map<number, IBackendLiveData>;
}

export function StreamEncoderSetupModal({
  opened,
  onClose,
  stream,
  backendMap,
}: StreamEncoderSetupModalProps) {
  const isReseller = useResellerBrand();
  const backendId = stream.stream?.core?.backendId ?? 0;
  const backend = backendMap.get(backendId);
  // White-label accounts must never see a streamhoster.com host here (the
  // white-label domain aliases the same ingest servers; legacy did the same).
  const rtmpUrl = backend ? ResellerBrand.rewriteServiceUrl(backend.rtmpServerUrl, isReseller) : 'N/A';
  const streamKeys = stream.publisherStreamKeys ?? [];
  const outputs = stream.outputs ?? [];
  const hasMultipleOutputs = streamKeys.length > 1;

  // Build a lookup map: mediaStreamOutputId → output data
  const outputMap = new Map<number, StreamOutputData>();
  for (const output of outputs) {
    outputMap.set(output.mediaStreamOutputId, output);
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Encoder Setup"
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Configure your encoder with the settings below for &ldquo;{stream.media.title}&rdquo;.
        </Text>

        {/* RTMP Server URL */}
        <CopyableField label="RTMP Server URL" value={rtmpUrl} copyLabel="Copy URL" />

        <Divider />

        {/* Stream Keys */}
        {streamKeys.length > 0 ? (
          <Stack gap="lg">
            {hasMultipleOutputs && (
              <Text size="sm" fw={500} c="dimmed">
                This stream has {streamKeys.length} outputs. Configure your encoder with the specified bitrate and resolution for each stream key.
              </Text>
            )}
            {streamKeys.map((key, i) => {
              const output = outputMap.get(key.mediaStreamOutputId);
              return (
                <Box key={key.mediaStreamOutputId}>
                  <CopyableField
                    label={hasMultipleOutputs ? `Stream Key ${i + 1}` : 'Stream Key'}
                    value={key.streamKey}
                    copyLabel="Copy Key"
                  />
                  {hasMultipleOutputs && output && (
                    <OutputSpecs output={output} />
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed" fs="italic">
            No stream keys available. Keys are generated after the stream is provisioned.
          </Text>
        )}

        <Divider />

        {/* Encoder Requirements */}
        <Box>
          <Text size="sm" fw={600} mb="xs">Encoder Requirements</Text>
          <Stack gap={4}>
            <SpecRow label="Video Codec" value="H.264" />
            <SpecRow label="Keyframe Interval" value="2 seconds (required)" bold />
            <SpecRow label="Audio Codec" value="AAC, Stereo" />
            <SpecRow label="Audio Sample Rate" value="48 kHz" />
          </Stack>
        </Box>

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Copyable read-only field with inline copy button */
function CopyableField({ label, value, copyLabel }: { label: string; value: string; copyLabel: string }) {
  return (
    <div>
      <TextInput
        label={label}
        value={value}
        readOnly
        styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
      />
      <Group mt="xs">
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Button
              size="xs"
              variant="light"
              leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              color={copied ? 'teal' : 'blue'}
              onClick={copy}
            >
              {copied ? 'Copied' : copyLabel}
            </Button>
          )}
        </CopyButton>
      </Group>
    </div>
  );
}

/** Per-output resolution and bitrate specs shown below each stream key */
function OutputSpecs({ output }: { output: StreamOutputData }) {
  return (
    <Group mt="xs" gap="lg">
      <SpecRow label="Resolution" value={`${output.videoWidth}x${output.videoHeight}`} />
      <SpecRow label="Video" value={`${output.videoKbps} kbps`} />
      <SpecRow label="Audio" value={`${output.audioKbps} kbps`} />
      <SpecRow label="Profile" value={StreamUtils.recommendedH264Profile(output.videoHeight)} />
    </Group>
  );
}

/** Compact label: value display */
function SpecRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Text size="sm">
      <Text component="span" c="dimmed">{label}:</Text>{' '}
      <Text component="span" fw={bold ? 700 : 500}>{value}</Text>
    </Text>
  );
}
